import i18n from "@/i18n";
import store from "@/stores/store";
import moment from "moment";

/**
 * Obtém o formato da data baseado na língua.
 * @returns Formato de data.
 */
const GetCurrentDateFormat = function()
{
	return i18n.locale === "en-US" ? "MM/DD/YYYY" : "DD/MM/YYYY";
};

/**
 * Obtém o formato da data e hora baseado na língua.
 * @returns Formato de data e hora.
 */
const GetCurrentDateTimeFormat = function()
{
	return i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY HH:mm:ss";
};

/**
 * Obtém o offset do usuário direto da store.
 * @returns O valor do deslocamento do usuário.
 */
const GetUserOffset = function()
{
	const userOffset = () => store.getters["UserModule/UserPreferencesModule/userOffset"];
	const offset = userOffset();
	return offset;
};

/**
 * Obtém um intervalo de datas ao redor de uma data base.
 * @param {string} dateTime - Data base.
 * @param {Object} - Intervalo antes e depois da data base.
 * - `value` {number}: Valor do intervado.
 * - `unit` {string}: Unidades do intervalo aceito pelo moment.
 * @returns Intervalo no formato ISO.
 */
const GetRangeDateAround = function(dateTime, range = { value: 7, unit: "days" })
{
	const momentDate = moment.utc(dateTime);

	return {
		start: momentDate.clone().subtract(range.value, range.unit).format(),
		end:   momentDate.clone().add(range.value, range.unit).format()
	};
};

/**
 * O v-date-picker apenas aceita datas no formato YYYY-MM-DD independente da língua.
 * Esse método serve para converter uma data qualquer para o formato do picker.
 * @param {string} date - Data a ser convertida
 * @returns Data no formato YYYY-MM-DD.
 */
const ParseDateToDatePickerFormat = function(date)
{
	const dateFormat = GetCurrentDateFormat();

	const momentObject = moment(date, dateFormat);

	return momentObject.format("YYYY-MM-DD");
};

/**
 * Converte uma data e hora local (no formato conforme a localidade do usuário)
 * para um formato ISO UTC sem o sufixo 'Z'.
 * 
 * @param {string} localDateTime - Data e hora local no formato conforme a língua do usuário.
 * Exemplo: "13/05/2025 14:30:00" para pt-BR, ou "05/13/2025 02:30:00 PM" para en-US.
 * 
 * @returns {string} Data e hora em formato UTC (ISO 8601) sem o sufixo 'Z'.
 * Exemplo: "2025-05-13T17:30:00"
 */
const ToUTCDateTime = function(localDateTime)
{
	const offset = NormalizeOffset(GetUserOffset());
	const dateTimeFormat = GetCurrentDateTimeFormat();

	const localMoment = moment(localDateTime, dateTimeFormat);
	const momentWithOffset = localMoment.utcOffset(offset, true);

	return momentWithOffset.utc().format().replace("Z", "");
};

/**
 * Converte uma data e hora UTC (em formato ISO) para uma data e hora local,
 * de acordo com o offset do usuário e o formato de exibição baseado na localidade.
 * 
 * @param {string} utcDateTime - Data e hora em formato UTC (ISO 8601), ex: "2025-05-13T17:30:00".
 * 
 * @returns {string} Data e hora convertida para o fuso horário local e formatada conforme a língua do usuário.
 * Exemplo: "13/05/2025 14:30:00" para pt-BR, ou "05/13/2025 02:30:00 PM" para en-US.
 */
const ToLocalDateTime = function(utcDateTime)
{
	const offset = NormalizeOffset(GetUserOffset());
	const dateTimeFormat = GetCurrentDateTimeFormat();

	const utcMoment = moment.utc(utcDateTime);
	const localMoment = utcMoment.utcOffset(offset);

	return localMoment.format(dateTimeFormat);
};

/**
 * Garante que o offset tenha um sinal (+ ou -).
 * Exemplos:
 *   "02:00" → "+02:00"
 *   "-03:00" → "-03:00" (não muda)
 *   "00:00" → "+00:00"
 */
const NormalizeOffset = function(rawOffset)
{
	if (rawOffset.startsWith("+") || rawOffset.startsWith("-"))
	{
		return rawOffset;
	}
	return `+${rawOffset}`;
};

/**
 * Obtém a data e hora atual em UTC, mas calculada baseada no Offset do Usuário.
 * * @returns {string} Data UTC formatada para o Backend (YYYY-MM-DDTHH:mm:ss)
 */
const GetCurrentUTCDateTimeISO = function()
{
	const nowLocalString = moment().format(GetCurrentDateTimeFormat());

	return ToUTCDateTime(nowLocalString);
};

/**
 * Processa a data de vigência inicial para preencher os campos do componente.
 * Substitui a lógica manual de inicialização no componente Vue.
 * * @param {string} effectiveSince - A string de data vinda do model.
 * @returns {Object|null} Objeto com dados formatados para UI e Model ou null se inválido.
 */
const GetInitialEffectiveDateData = function(effectiveSince)
{
	let momentObj;

	// 1. Se estiver vazio, cria um "Agora"
	if (!effectiveSince)
	{
		momentObj = moment();
	}
	// 2. Se tem "T", é ISO (padrão universal)
	else if (effectiveSince.includes("T"))
	{
		momentObj = moment(effectiveSince);
	}
	// 3. Se NÃO tem "T", é formato visual/backend (com espaço)
	else
	{
		const formatPattern = GetCurrentDateTimeFormat();
		momentObj = moment(effectiveSince, formatPattern);
	}

	if (!momentObj.isValid()) return null;

	const displayFormat = GetCurrentDateFormat();

	return {
		iso:         momentObj.format(),
		datePicker:  momentObj.format("YYYY-MM-DD"),
		timePicker:  momentObj.format("HH:mm"),
		displayDate: momentObj.format(displayFormat)
	};
};

export default {
	GetCurrentDateFormat,
	GetCurrentDateTimeFormat,
	GetUserOffset,
	GetRangeDateAround,
	ParseDateToDatePickerFormat,
	ToUTCDateTime,
	ToLocalDateTime,
	NormalizeOffset,
	GetCurrentUTCDateTimeISO,
	GetInitialEffectiveDateData
};
